/**
 * FDV — ventana de visibilidad de la etiqueta 3x2 fuera de la landing (`blocks/fdv-promo-card-badge.liquid`, modo ventana).
 *
 * La etiqueta es un custom element `<fdv-promo-badge-chip>`: Liquid (server) decide su estado inicial (`hidden` o no) y este
 * módulo lo recalcula en el navegador para que aparezca/desaparezca sola en la frontera exacta, sin recargar.
 *
 * Por qué un custom element: hay tarjetas que llegan DESPUÉS de cargar la página (recomendados de la ficha, filtros,
 * paginación). `connectedCallback` se dispara en cada inserción, así cada chip se evalúa solo cuando llega, sin
 * MutationObserver (lección del post-mortem del 21-sep, doc 26).
 *
 * Reglas (las mismas que `fdv-promo-state.js`, duplicadas a propósito para no importar sin `?v=` — cache de la CDN):
 *   · Instantes ISO con offset explícito → no dependen de la zona del dispositivo. Fin de ventana exclusivo.
 *   · Si el reloj del dispositivo difiere >6 h del server (`data-server-now`), manda el estado del server.
 *   · `?promo_at=` fija el instante (solo previsualizar; se desactiva con `data-allow-preview="false"`).
 *   · Escrituras idempotentes + un timer a la próxima frontera + visibilitychange.
 *
 * El script solo COMUNICA: Shopify gobierna la vigencia real del descuento.
 */

const MAX_TIMEOUT = 2 ** 31 - 1;
const HAS_OFFSET = /(?:[zZ]|[+-]\d\d:?\d\d)$/;
const SKEW_MS = 6 * 3600 * 1000;

const windowsCache = new Map();
const chips = new Set();
let timer = 0;

function parseInstant(raw) {
  const s = String(raw ?? '').trim();
  return HAS_OFFSET.test(s) ? Date.parse(s) : NaN; // sin offset = ambiguo → se rechaza
}

function parseWindows(raw) {
  if (windowsCache.has(raw)) return windowsCache.get(raw);
  const windows = String(raw ?? '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [a, b] = line.split('/');
      return { start: parseInstant(a), end: parseInstant(b) };
    })
    .filter((w) => Number.isFinite(w.start) && Number.isFinite(w.end) && w.end > w.start);
  windowsCache.set(raw, windows);
  return windows;
}

/** Instante fijo de `?promo_at=` (ms) o NaN. */
function readOverride(el) {
  if (el.dataset.allowPreview === 'false') return NaN;
  const raw = new URLSearchParams(location.search).get('promo_at');
  if (!raw) return NaN;
  let v = raw.trim().replace(' ', 'T');
  if (!HAS_OFFSET.test(v)) v += '-06:00'; // sin offset → hora de Ciudad de México, nunca la del dispositivo
  return Date.parse(v);
}

/** Instante con el que se evalúa un chip; NaN = no tocarlo (manda el estado del server). */
function instantFor(el) {
  const override = readOverride(el);
  if (Number.isFinite(override)) return override;
  const serverNow = Number(el.dataset.serverNow) * 1000;
  if (Number.isFinite(serverNow) && Math.abs(Date.now() - serverNow) > SKEW_MS) return NaN; // reloj roto → server
  return Date.now();
}

function refresh() {
  clearTimeout(timer);
  let next = Infinity;
  let live = false;
  const now = Date.now();
  for (const el of chips) {
    if (el.dataset.fdvBadgeWindows === undefined) continue; // editor de temas: siempre visible
    const at = instantFor(el);
    if (!Number.isFinite(at)) continue;
    const windows = parseWindows(el.dataset.fdvBadgeWindows);
    const visible = windows.some((w) => at >= w.start && at < w.end);
    if (el.hidden === visible) el.hidden = !visible; // idempotente: no escribe si no cambia
    if (!Number.isFinite(readOverride(el))) {
      live = true;
      for (const w of windows) {
        if (w.start > now && w.start < next) next = w.start;
        if (w.end > now && w.end < next) next = w.end;
      }
    }
  }
  if (live && Number.isFinite(next)) timer = setTimeout(refresh, Math.min(next - now + 250, MAX_TIMEOUT));
}

if (typeof HTMLElement !== 'undefined' && typeof customElements !== 'undefined') {
  class FdvPromoBadgeChip extends HTMLElement {
    connectedCallback() {
      chips.add(this);
      refresh();
    }

    disconnectedCallback() {
      chips.delete(this);
    }
  }

  if (!customElements.get('fdv-promo-badge-chip')) customElements.define('fdv-promo-badge-chip', FdvPromoBadgeChip);

  // Los timers se frenan en pestañas ocultas → al volver, recalcular.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) refresh();
  });
}
